import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Trophy, Users, Sparkles, ArrowLeft, ExternalLink } from "lucide-react";
import { API, useAuth } from "@/App";

export default function Referrals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    Promise.all([
      fetch(`${API}/referrals/my-stats`, { credentials: "include" }).then(r => r.json()),
      fetch(`${API}/referrals/leaderboard`).then(r => r.json()),
    ]).then(([s, l]) => {
      setStats(s);
      setLeaders(l.leaders || []);
      setLoading(false);
    });
  }, [user, navigate]);

  const copyCode = () => {
    if (!stats?.my_code?.code) return;
    navigator.clipboard.writeText(stats.my_code.code);
    toast.success("Code copied!");
  };

  const copyLink = () => {
    if (!stats?.my_code?.code) return;
    const url = `${window.location.origin}/register?ref=${stats.my_code.code}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied!");
  };

  const shareNative = async () => {
    if (!stats?.my_code?.code) return;
    const url = `${window.location.origin}/register?ref=${stats.my_code.code}`;
    const text = `Join me on OurSpacez — use my code ${stats.my_code.code} for 30 days of OurSpacez Gold free!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join OurSpacez", text, url });
      } catch (e) {
        // user cancelled
      }
    } else {
      copyLink();
    }
  };

  if (loading) return <div className="min-h-screen bg-black text-white p-8">Loading…</div>;
  const code = stats?.my_code?.code || "—";
  const usesLeft = stats?.my_code ? (stats.my_code.max_uses - stats.my_code.current_uses) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0014] via-[#1a0a2e] to-[#000] py-8 px-4 text-white">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="text-pink-300 hover:text-pink-100 flex items-center gap-1 mb-4 text-sm font-bold"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Gift className="w-10 h-10 text-yellow-400" />
          <div>
            <h1 className="text-4xl font-black tracking-tight">Refer & Earn</h1>
            <p className="text-pink-300 text-sm">Share your code · You earn Gold · They earn Gold</p>
          </div>
        </div>

        {/* My code card */}
        <div className="rounded-lg border-4 border-black bg-gradient-to-br from-fuchsia-500 to-purple-700 p-6 shadow-[8px_8px_0_#000] mb-6" data-testid="my-code-card">
          <p className="text-yellow-200 text-xs font-black uppercase mb-2">Your code</p>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-black border-2 border-yellow-400 rounded px-4 py-3 text-yellow-300 font-mono font-black text-2xl tracking-wider" data-testid="my-code-text">
              {code}
            </div>
            <Button onClick={copyCode} className="bg-white text-black font-black border-2 border-black hover:bg-gray-100" data-testid="copy-code-btn">
              <Copy className="w-4 h-4 mr-2" /> Copy code
            </Button>
            <Button onClick={copyLink} className="bg-yellow-400 text-black font-black border-2 border-black hover:bg-yellow-300" data-testid="copy-link-btn">
              <ExternalLink className="w-4 h-4 mr-2" /> Copy link
            </Button>
            <Button onClick={shareNative} className="bg-pink-500 hover:bg-pink-400 text-white font-black border-2 border-black">
              <Sparkles className="w-4 h-4 mr-2" /> Share
            </Button>
          </div>
          <p className="text-pink-100 text-xs mt-4">
            ✨ Each successful signup = <strong>30 days of OurSpacez Gold</strong> for both of you.<br />
            🎟️ Uses left: <strong>{usesLeft}</strong> · Total used: <strong>{stats?.my_code?.current_uses ?? 0}</strong>
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Stat icon={<Users className="text-pink-400" />} label="Successful referrals" value={stats?.total_successful_referrals ?? 0} />
          <Stat icon={<Sparkles className="text-yellow-400" />} label="Gold days earned" value={stats?.total_days_earned ?? 0} />
          <Stat icon={<Trophy className="text-amber-400" />} label="Leaderboard rank" value={
            (() => {
              const rank = leaders.findIndex(l => l.user_id === user?.user_id);
              return rank >= 0 ? `#${rank + 1}` : "—";
            })()
          } />
        </div>

        {/* Recent referees */}
        <h2 className="text-xl font-black mb-3">Recent signups via your code</h2>
        {(stats?.recent_referrals?.length ?? 0) === 0 ? (
          <p className="text-pink-200/60 italic mb-8" data-testid="recent-empty">
            No one has used your code yet. Share it!
          </p>
        ) : (
          <div className="space-y-2 mb-8">
            {stats.recent_referrals.map(r => (
              <div key={r.referral_id} className="bg-white/5 border border-pink-500/30 rounded p-3 flex items-center justify-between text-sm" data-testid={`referral-${r.referral_id}`}>
                <span><strong>{r.referee_name || "User"}</strong> joined</span>
                <span className="text-pink-300 text-xs">+{r.referrer_reward_days}d Gold · {new Date(r.redeemed_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}

        {/* Leaderboard */}
        <h2 className="text-xl font-black mb-3 flex items-center gap-2">
          <Trophy className="text-amber-400" /> Top Referrers
        </h2>
        {leaders.length === 0 ? (
          <p className="text-pink-200/60 italic">No referrals yet — be the first!</p>
        ) : (
          <div className="space-y-2">
            {leaders.map((l, idx) => (
              <div key={l.user_id} className={`flex items-center gap-3 p-3 rounded border-2 ${
                l.user_id === user?.user_id ? "border-yellow-400 bg-yellow-400/10" : "border-pink-500/30 bg-white/5"
              }`} data-testid={`leader-${idx}`}>
                <div className="text-xl font-black w-8 text-center">
                  {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}
                </div>
                {l.picture ? (
                  <img src={l.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-pink-500/30" />
                )}
                <div className="flex-1">
                  <p className="font-bold">{l.name}</p>
                </div>
                <div className="text-yellow-300 font-black">{l.count} ✨</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-white/5 border-2 border-pink-500/30 rounded-lg p-4">
      <div className="flex items-center gap-2 text-xs text-pink-200 uppercase mb-1">
        {icon} <span>{label}</span>
      </div>
      <div className="text-3xl font-black">{value}</div>
    </div>
  );
}
